import styled from '@emotion/styled/macro';
import Divider from '@mui/material/Divider';
import {
  Button, Checkbox, Dialog, DialogActions, DialogContent, FormControlLabel, IconButton, Stack, Tooltip, Typography,
} from '@mui/material';
import React, {
  Dispatch, SetStateAction, useCallback, useEffect, useMemo, useState,
} from 'react';
import { MdHelpOutline, MdOutlineClose } from 'react-icons/md';
import Grid from '@mui/material/Grid';
import { DataGrid } from '@mui/x-data-grid';
import { toast } from 'react-toastify';
import { toGroupMap } from 'common/util/generalUtils';
import { useCompanyFleets } from 'accountUsers/hooks/useCompanyFleets';
import { useAccountAircraft } from '../../../aircraft/hooks/useAccountAircraft';
import { SimpleSearch } from '../../../common/components/SimpleSearch';
import { useModalStore } from '../../../common/hooks/useModals';
import useModifyAircraftFleetAssignments, { ModifyAircraftFleetAssignmentsParams } from '../../hooks/useModifyAircraftFleetAssignments';

const ModalHeader = styled.div`
  font-size: 20px;
  padding: 12px;
`;

const ModalBody = styled.div`
  display: flex;
  gap: 9px;
  flex-direction: column;
`;

const ModalDivider = styled(Divider)`
`;

const ModalHeaderActionContainer = styled(Stack)`
  justify-content: space-between;
`;

const CloseButton = styled(Button)`
  font-size: 20px;
  color: black;
`;

const SubmitButton = styled(Button)`
  width: 120px;
`;

const DataHeader = styled(Typography)`
  font-size: 14px;
`;

const DataCell = styled(Typography)`
  font-size: 12px;
`;

const StyledDialogContent = styled(DialogContent)`
  padding-top: 8px;
  min-height: 50vh;
`;

const StyledDataGrid = styled(DataGrid)`
  height: 50vh;
  margin-top: 8px;
`;

const FleetContainerStack = styled(Stack)`
  height: 100%;
`;

const StyledFleetStack = styled(Stack)`
  margin-bottom: 8px;
  margin-right: 8px;
  max-height: 50vh;
  overflow-y: auto;
`;

const StyledIconButton = styled(IconButton)`
  margin-top: -3px;
  margin-left: -6px;
`;

const StyledCheckbox = styled(Checkbox)`
  padding-top: 0;
  padding-bottom: 0;
`;

type ViewProps = {
    handleClose: () => void;
    handleSubmit: () => void;
    firstName: string;
    lastName: string;
    fleets: Omit<Fleet, 'aircrafts'>[];
    aircrafts: (Aircraft & { selected: boolean; })[];
    search: string;
    setSearch: Dispatch<SetStateAction<string>>;
    showOnlyAssignedAircraft: boolean;
    setShowOnlyAssignedAircraft: Dispatch<SetStateAction<boolean>>;
    allFleetsSelected: boolean;
    setAllFleetsSelected: Dispatch<SetStateAction<boolean>>;
    selectedFleets: Set<number>;
    setSelectedFleets: Dispatch<SetStateAction<Set<number>>>;
    numberOfSelectedAircraft: number;
    hasNoFleets: boolean;
    isAircraftSelected: (fleetId: number) => boolean;
  switchToAircraftAssignment: () => void;
};

function View({
  handleClose, handleSubmit, firstName, lastName, fleets, aircrafts, search, setSearch, showOnlyAssignedAircraft, setShowOnlyAssignedAircraft,
  allFleetsSelected, setAllFleetsSelected, selectedFleets, setSelectedFleets, numberOfSelectedAircraft, hasNoFleets, isAircraftSelected,
  switchToAircraftAssignment,
}: ViewProps): React.JSX.Element {
  return (
    <Dialog
      open
      onClose={handleClose}
      maxWidth="lg"
      fullWidth
    >
      <ModalHeaderActionContainer spacing={2} direction="row">
        <ModalHeader>
          Modify Fleet Assignments -
          {' '}
          {`${firstName} ${lastName}`}
        </ModalHeader>
        <CloseButton variant="text" onClick={handleClose}>
          <MdOutlineClose />
        </CloseButton>
      </ModalHeaderActionContainer>
      <ModalDivider />
      <StyledDialogContent>
        <ModalBody>
          <Grid container>
            <Grid item xs={3}>
              <FleetContainerStack>
                <div>
                  <Button variant="outlined" onClick={switchToAircraftAssignment}>Change to Aircraft Assignment</Button>
                  <Tooltip
                    title={(
                      <Typography>
                        User will have access to specific aircraft which will only change if you explicitly update their permissions later
                      </Typography>
                      )}
                  >
                    <IconButton>
                      <MdHelpOutline fontSize="28px" />
                    </IconButton>
                  </Tooltip>
                </div>
                <Stack>
                  <Typography>
                    Fleet:
                    <Tooltip title={(
                      <Typography>
                        Custom fleets can be setup by clicking your name in the top right, selecting Account, then Fleets.
                        NOTE: If you navigate away from this screen you will need to start over.
                      </Typography>
                    )}
                    >
                      <StyledIconButton>
                        <MdHelpOutline />
                      </StyledIconButton>
                    </Tooltip>
                  </Typography>
                  <StyledFleetStack>
                    <FormControlLabel
                      control={(
                        <StyledCheckbox
                          checked={allFleetsSelected}
                          onChange={(e) => setAllFleetsSelected(e.target.checked)}
                          disabled={hasNoFleets}
                        />
                        )}
                      label="All Aircraft (current and future)"
                    />
                    {fleets.map((f) => (
                      <FormControlLabel
                        key={f.fleet_id}
                        control={(
                          <StyledCheckbox
                            checked={selectedFleets.has(f.fleet_id)}
                            onChange={(e) => {
                              setAllFleetsSelected(false);
                              setSelectedFleets((p) => {
                                if (e.target.checked) {
                                  p.add(f.fleet_id);
                                } else {
                                  p.delete(f.fleet_id);
                                }

                                return new Set([...p.values()]);
                              });
                            }}
                          />
                        )}
                        label={f.fleet_name}
                      />
                    ))}
                  </StyledFleetStack>
                </Stack>
              </FleetContainerStack>
            </Grid>
            <Grid item xs={9}>
              <Stack direction="row" justifyContent="space-between" alignItems="center">
                <Typography>
                  User will receive access to
                  {' '}
                  {numberOfSelectedAircraft}
                  {' '}
                  total aircraft
                </Typography>
                <FormControlLabel
                  control={(
                    <Checkbox
                      checked={showOnlyAssignedAircraft}
                      onChange={(e) => setShowOnlyAssignedAircraft(e.target.checked)}
                    />
                                    )}
                  label="Show Only Assigned Aircraft"
                />
              </Stack>
              <SimpleSearch search={search} setSearch={setSearch} placeHolder="Search Aircraft..." />
              <StyledDataGrid
                columns={[
                  {
                    renderHeader: () => (
                      <Checkbox
                        checked={allFleetsSelected}
                        disabled
                        indeterminate={!allFleetsSelected && selectedFleets.size > 0}
                      />
                    ),
                    field: 'selected',
                    renderCell: ({ row: { payment_id } }) => (
                      <Checkbox
                        checked={isAircraftSelected(payment_id as number)}
                        disabled
                      />
                    ),
                    sortable: false,
                  },
                  {
                    renderHeader: () => <DataHeader>Tail Number</DataHeader>,
                    field: 'tailnumber',
                    flex: 1,
                    renderCell: ({ row }) => (
                      <DataCell>
                        {row.reg_n_number}
                      </DataCell>
                    ),
                  },
                  {
                    renderHeader: () => <DataHeader>Serial Number</DataHeader>,
                    field: 'serial_number',
                    flex: 1,
                    renderCell: ({ row: { serial_number } }) => <DataCell>{serial_number}</DataCell>,
                  },
                  {
                    renderHeader: () => <DataHeader>Make</DataHeader>,
                    field: 'manufacturer',
                    flex: 1,
                    renderCell: ({ row: { manufacturer } }) => <DataCell>{manufacturer}</DataCell>,
                  },
                  {
                    renderHeader: () => <DataHeader>Model</DataHeader>,
                    field: 'model',
                    flex: 1,
                    renderCell: ({ row: { model } }) => <DataCell>{model}</DataCell>,
                  },
                ]}
                rows={aircrafts}
                disableRowSelectionOnClick
                getRowId={({ payment_id }) => payment_id as number}
                hideFooter
              />
            </Grid>
          </Grid>
        </ModalBody>
      </StyledDialogContent>
      <DialogActions>
        <Stack spacing={2} direction="row" justifyContent="end" width="100%">
          <Button onClick={handleClose}>Cancel</Button>
          <SubmitButton variant="contained" color="primary" onClick={handleSubmit}>
            Save
          </SubmitButton>
        </Stack>
      </DialogActions>
    </Dialog>
  );
}

type ModifyFleetAssignmentsModalData = {
  userId: number;
  companyId: number;
  firstName: string;
  lastName: string;
  assignedAircrafts: number[];
  allFleetsSelected: boolean;
  assignedFleets: number[];
  refreshUserList: () => void;
};

type ModifyFleetAssignmentsModalStore = {
    modifyFleetAssignments: ModifyFleetAssignmentsModalData;
    setModifyFleetAssignmentsState: (state: boolean) => void;
    setModifyAircraftAssignmentsState: (state: boolean, data: ModifyFleetAssignmentsModalData) => void;
};

function Model(): React.JSX.Element {
  const { modifyFleetAssignments, setModifyFleetAssignmentsState, setModifyAircraftAssignmentsState } = useModalStore() as ModifyFleetAssignmentsModalStore;
  const { aircraft: aircrafts } = useAccountAircraft({
    company_id: modifyFleetAssignments.companyId,
    min: 0,
    max: 100_000_000,
    searchText: '',
  }) as { aircraft: Aircraft[] };
  const { rows: allFleetsWithDefault } = useCompanyFleets(modifyFleetAssignments.companyId);
  const [search, setSearch] = useState('');
  const [showOnlyAssignedAircraft, setShowOnlyAssignedAircraft] = useState(false);
  const [allFleetsSelected, setAllFleetsSelected] = useState(modifyFleetAssignments.allFleetsSelected);
  const [selectedFleets, setSelectedFleets] = useState<Set<number>>(new Set());
  const modifyFleetAssignmentsMutation = useModifyAircraftFleetAssignments(modifyFleetAssignments.userId);

  // Note: 'All Aircraft' should be irrelevant now.  This was partof Janky fleet management, which has been phased out.
  const allFleets = useMemo(() => allFleetsWithDefault.filter((x) => x.fleet_name !== 'All Aircraft'), [allFleetsWithDefault]);

  const aircraftNumberToFleetIdMap = useMemo(
    () => toGroupMap(
      allFleets.flatMap((x) => x.enabled_aircrafts ?? []).filter(Boolean),
      (x) => x.aircraft_id,
      (x) => x.fleet_id,
    ),
    [allFleets],
  );

  const allAircrafts = useMemo<ViewProps['aircrafts']>(
    () => aircrafts
      .map((x) => ({
        ...x,
        selected: allFleetsSelected || (aircraftNumberToFleetIdMap.get(x.payment_id)?.some((y) => selectedFleets.has(y)) ?? false),
      })),
    [aircrafts, allFleetsSelected, selectedFleets, aircraftNumberToFleetIdMap],
  );

  const allAircraftsWithSelected = useMemo(
    () => allAircrafts
      .filter((x) => !showOnlyAssignedAircraft || x.selected),
    [allAircrafts, showOnlyAssignedAircraft],
  );

  const filteredAircrafts = useMemo(() => {
    const normalizedSearch = search.toLowerCase().split(' ');
    return allAircraftsWithSelected.filter((x) => normalizedSearch.every((s) => x.reg_n_number.toLowerCase().includes(s)
        || x.model.toLowerCase().includes(s)
        || x.serial_number.toLowerCase().includes(s)
        || x.manufacturer.toLowerCase().includes(s)));
  }, [search, allAircraftsWithSelected]);

  const numberOfSelectedAircraft = useMemo(
    () => allAircraftsWithSelected.filter((x) => x.selected).length,
    [allAircraftsWithSelected],
  );

  useEffect(() => {
    if (!allFleetsSelected) {
      return;
    }

    setSelectedFleets(new Set());
  }, [allFleetsSelected]);

  useEffect(() => {
    if (modifyFleetAssignments.allFleetsSelected || allFleets.length === 0) {
      setAllFleetsSelected(true);
      return;
    }

    setSelectedFleets(new Set(modifyFleetAssignments.assignedFleets));
  }, [modifyFleetAssignments, allFleets]);

  const isAircraftSelected = useCallback((paymentId: number) => {
    if (allFleetsSelected) {
      return true;
    }

    const matchedFleet = aircraftNumberToFleetIdMap.get(paymentId);
    if (!matchedFleet) {
      return false;
    }

    return matchedFleet.some((x) => selectedFleets.has(x));
  }, [allFleetsSelected, aircraftNumberToFleetIdMap, selectedFleets]);

  const handleClose = (): void => setModifyFleetAssignmentsState(false);
  const handleSubmit = (): void => {
    if (!allFleetsSelected && selectedFleets.size < 1) {
      toast.error('You must select at least one fleet');
      return;
    }

    const params: ModifyAircraftFleetAssignmentsParams = {
      company_id: modifyFleetAssignments.companyId,
      aircraft_assignment_method: allFleetsSelected ? 'all' : 'fleet',
      aircrafts: null,
      fleets: allFleetsSelected ? null : [...selectedFleets.values()],
    };

    modifyFleetAssignmentsMutation.mutate(params, {
      onSuccess: () => {
        toast.success('User fleet assignments updated');
        modifyFleetAssignments.refreshUserList();
        handleClose();
      },
      onError: () => {
        toast.error('Error updating user');
      },
    });
  };

  const viewProps: ViewProps = {
    handleClose,
    handleSubmit,
    firstName: modifyFleetAssignments.firstName,
    lastName: modifyFleetAssignments.lastName,
    fleets: allFleets,
    aircrafts: filteredAircrafts,
    search,
    setSearch,
    showOnlyAssignedAircraft,
    setShowOnlyAssignedAircraft,
    allFleetsSelected,
    setAllFleetsSelected,
    selectedFleets,
    setSelectedFleets,
    numberOfSelectedAircraft,
    hasNoFleets: allFleets.length === 0,
    isAircraftSelected,
    switchToAircraftAssignment: () => {
      handleClose();
      setModifyAircraftAssignmentsState(true, modifyFleetAssignments);
    },
  };

  return <View {...viewProps} />;
}

export default Model;
export { View };
